import { cn } from "cn";
import { Pause, Play } from "lucide-react";
import type * as React from "react";
import { useEffect, useRef } from "react";
import { createSilenceDetector } from "../lib/silence";

const BAR = 3;
const GAP = 2.5;

/**
 * v3.2 recording waveform (apps/web V3Waveform): recent microphone levels as
 * bars (older ones lighter), the rest of the track as a dotted line. Draws
 * fixed `levels`, or reads them from `stream` with an AnalyserNode. Draws in
 * the current text colour (brand by default).
 */
export function Waveform({
  levels,
  stream,
  paused = false,
  onSilentChange,
  label,
  className,
}: {
  /** Fixed levels (0–1), e.g. for stories and screenshots. */
  levels?: number[];
  /** A live microphone stream; ignored when `levels` is set. */
  stream?: MediaStream | null;
  paused?: boolean;
  onSilentChange?: (silent: boolean) => void;
  label: string;
  className?: string;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pausedRef = useRef(paused);
  // Counts pauses so a pause and resume between frames still resets the timer.
  const pauses = useRef(0);
  if (paused && !pausedRef.current) pauses.current++;
  pausedRef.current = paused;
  const onSilentChangeRef = useRef(onSilentChange);
  onSilentChangeRef.current = onSilentChange;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    let values = levels ? [...levels] : [];
    let audio: AudioContext | undefined;
    let analyser: AnalyserNode | undefined;
    let samples: Uint8Array<ArrayBuffer> | undefined;
    if (!levels && stream?.getAudioTracks().length) {
      try {
        audio = new AudioContext();
        analyser = audio.createAnalyser();
        analyser.fftSize = 512;
        audio.createMediaStreamSource(stream).connect(analyser);
        samples = new Uint8Array(analyser.fftSize);
      } catch {
        analyser = undefined;
      }
    }
    const detector = createSilenceDetector();
    let silent = false;
    let seenPauses = pauses.current;
    let frame = 0;
    let last = 0;
    const draw = (time: number): void => {
      frame = requestAnimationFrame(draw);
      if (time - last < 70) return;
      last = time;
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * ratio || canvas.height !== height * ratio) {
        canvas.width = width * ratio;
        canvas.height = height * ratio;
      }
      const slots = Math.max(1, Math.floor(width / (BAR + GAP)));
      if (seenPauses !== pauses.current) {
        seenPauses = pauses.current;
        detector.reset();
        if (silent) {
          silent = false;
          onSilentChangeRef.current?.(false);
        }
      }
      if (analyser && samples && !pausedRef.current) {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const value of samples) sum += ((value - 128) / 128) ** 2;
        const level = Math.min(1, Math.sqrt(sum / samples.length) * 5);
        values.push(level);
        const nextSilent = detector.sample(level, time);
        if (nextSilent !== silent) {
          silent = nextSilent;
          onSilentChangeRef.current?.(silent);
        }
      }
      // The bars fill the left part; the dotted rest is the time to come.
      const room = Math.ceil(slots * 0.62);
      if (values.length > room) values = values.slice(-room);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = getComputedStyle(canvas).color;
      values.forEach((level, index) => {
        const bar = Math.max(3, level * height);
        context.globalAlpha = 0.3 + 0.7 * ((index + 1) / values.length);
        context.beginPath();
        context.roundRect(
          index * (BAR + GAP),
          (height - bar) / 2,
          BAR,
          bar,
          BAR / 2,
        );
        context.fill();
      });
      context.globalAlpha = 0.55;
      for (let index = values.length; index < slots; index++) {
        context.beginPath();
        context.arc(
          index * (BAR + GAP) + BAR / 2,
          height / 2,
          1,
          0,
          Math.PI * 2,
        );
        context.fill();
      }
      context.globalAlpha = 1;
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      // The next detector starts quiet and only reports changes.
      if (silent) onSilentChangeRef.current?.(false);
      void audio?.close().catch(() => undefined);
    };
  }, [stream, levels]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={label}
      className={cn("h-8 w-full text-v3-brand", className)}
      role="img"
    />
  );
}

/** mm:ss, or h:mm:ss from an hour. */
export const formatClock = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h ? `${h}:` : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * The floating recorder pill (apps/web RecorderV32's bottom bar): waveform,
 * the red dot and time, Pause/Resume and Stop. Presentational: the recorder
 * engine owns the state and passes callbacks.
 */
export function RecorderBar({
  seconds,
  paused,
  onPause,
  onResume,
  onStop,
  onSilentChange,
  levels,
  stream,
  labels,
  controls = true,
  className,
}: {
  seconds: number;
  paused: boolean;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSilentChange?: (silent: boolean) => void;
  levels?: number[];
  stream?: MediaStream | null;
  labels: { micLevel: string; pause: string; resume: string; stop: string };
  /**
   * Pause and Stop are not offered yet (live transcription still connecting,
   * or the stop confirmation is open): the waveform and time stay.
   */
  controls?: boolean;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "flex h-[60px] items-center gap-2.5 rounded-full bg-v3-panel py-2 pl-4 pr-2 font-v3 text-v3-text shadow-v3-float ring-1 ring-v3-border",
        className,
      )}
      data-testid="recorder-bar"
    >
      <Waveform
        className="min-w-0 flex-1"
        label={labels.micLevel}
        levels={levels}
        paused={paused}
        stream={stream}
        onSilentChange={onSilentChange}
      />
      <p className="flex shrink-0 items-center gap-1.5 font-mono text-[15px] tabular-nums text-v3-text">
        <span
          aria-hidden
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            paused ? "bg-v3-text-3" : "bg-v3-rec",
          )}
        />
        {formatClock(seconds)}
      </p>
      {controls && (
        <>
          <button
            aria-label={paused ? labels.resume : labels.pause}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-v3-surface-2 text-v3-text transition-colors hover:bg-v3-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            type="button"
            onClick={paused ? onResume : onPause}
          >
            {paused ? (
              <Play aria-hidden className="h-4 w-4 fill-current" />
            ) : (
              <Pause aria-hidden className="h-4 w-4 fill-current" />
            )}
          </button>
          <button
            className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-v3-rec-tint px-4 text-[15px] font-semibold text-v3-rec-text transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            type="button"
            onClick={onStop}
          >
            <span aria-hidden className="h-3 w-3 rounded-[3px] bg-v3-rec" />
            {labels.stop}
          </button>
        </>
      )}
    </div>
  );
}
